import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// 🛰️ Unified types to resolve property mismatches
import { AppState, AppAction, KanbanTask, TaskStatus, BoardConfig, TemplateType, DataScope } from '@/types'; 

// --- Feature Imports ---
import { useTaskState } from '../features/kanban-engine/hooks/useTaskState';
import { groupTasksByContext } from '../features/kanban-engine/utils/grouping';

// --- Shared Components ---
import { Button } from '@/components/ui/Button';
import {
  ShoppingCart, History, Utensils, Filter,
  Sparkles, CheckCircle2, Pizza, ChevronRight,
  Check, Settings, X, Shield, Users, Layers, Lock, Pencil, Trash2
} from 'lucide-react';
import { TabBar, TabItem } from '@/components/ui/TabBar';
import { EmptyState } from '@/components/ui/EmptyState';

// --- Local Feature Bricks ---
import { InlineAddGrocery } from './InlineAddGrocery';
import { EditGroceryItem } from '@/components/EditGroceryItem';
import { CategoryManager } from '../features/inventory/CategoryManager';
import { guessCategory } from '../features/inventory/utils/groceryCategories';
import { useAiAction } from '@/hooks/useAiAction';

interface InventoryProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const GROCERY_BOARD: BoardConfig = {
  id: 'household-groceries-001',
  title: 'Groceries',
  templateType: TemplateType.GROCERY,
  allowedStatuses: ['todo', 'in_progress', 'pantry', 'history'],
  dataScope: 'BOTH', // Added missing property
  icon: 'shopping-cart', // Added missing property (example icon)
  color: '#10B981', // Added missing property (example color)
  visibility: 'member',
  isCollapsed: false,
  defaultGroupBy: 'category',
  activeFilters: []
};

export const Inventory: React.FC<InventoryProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const currentUserId = state.currentUser?.id || 'guest';
  const currentHouseholdId = state.currentHouseholdId;
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  
  // --- 🛡️ HYBRID SCOPE: Driven by global header toggle ---
  const activeScope: DataScope = state.activeDataScope ?? (currentHouseholdId ? 'BOTH' : 'PERSONAL');

  // 🤖 AI category suggestion
  const [aiCategorySuggestion, setAiCategorySuggestion] = useState<{ name: string; category: string } | null>(null);
  const { callAction: callAiAction } = useAiAction(currentHouseholdId);

  // --- UI STATE ---
  // --- 🛰️ ENGINE INTEGRATION ---
  const inventoryData = (state.inventory as KanbanTask[]) || [];
  const { tasks, moveTask, createTask, consumeQuantity, deleteTask, updateTask, migrateTaskScope, isOnline } = useTaskState(
    inventoryData, 
    currentUserId,
    dispatch,
    currentHouseholdId,
    activeScope,
    state.currentUser?.permissions // Use currentUser.permissions here
  );

  const [activeTab, setActiveTab] = useState<TaskStatus>('todo');
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  // --- 🧠 PREDICTIVE SUGGESTIONS (Scope Aware) ---
  const suggestions = useMemo(() => {
    const todayMillis = Date.now();
    return tasks.filter((t: KanbanTask) => {
      // 🛡️ Filter suggestions based on privacy boundary
      if (activeScope === 'PERSONAL' && t.scope !== 'personal') return false;
      if (activeScope === 'SHARED' && t.scope !== 'household') return false;

      const consumption = t.consumption;
      if (t.templateType !== 'grocery' || !consumption?.lastAddedHistory || t.status !== 'history') return false;
      
      const isAlreadyActive = tasks.some((active: KanbanTask) => 
        active.title.toLowerCase() === t.title.toLowerCase() && active.status !== 'history'
      );
      if (isAlreadyActive) return false;

      const lastDate = new Date(consumption.lastAddedHistory);
      const cycle = consumption.averageCycle || 14; 
      const predictedDate = new Date(lastDate);
      predictedDate.setDate(lastDate.getDate() + cycle);
      
      const diffDays = Math.ceil((predictedDate.getTime() - todayMillis) / (1000 * 60 * 60 * 24));
      return diffDays <= 2;
    });
  }, [tasks, activeScope]);

  // --- 📂 DYNAMIC GROUPING & FILTERING ---
  const groupedData = useMemo(() => {
    // 🛡️ Filter by Active Scope before grouping
    const scopedTasks = tasks.filter((t: KanbanTask) => {
      const matchStatus = t.status === activeTab;
      let matchScope = true;
      if (activeScope === 'PERSONAL') matchScope = t.scope === 'personal';
      if (activeScope === 'SHARED') matchScope = t.scope === 'household';
      
      // 🔒 Privacy Guard: Never leak personal items of other users
      const privacyCheck = t.scope === 'personal' ? t.ownerId === currentUserId : true;

      return matchStatus && matchScope && privacyCheck;
    });

    return groupTasksByContext(scopedTasks, GROCERY_BOARD, selectedCategory, currentUserId, currentHouseholdId, activeScope);
  }, [tasks, activeTab, selectedCategory, activeScope, currentUserId]);

  const categories = useMemo(() => {
    const tags = tasks
      .filter((t: KanbanTask) => t.boardId === GROCERY_BOARD.id)
      .flatMap((t: KanbanTask) => t.tags || []);
    return ['ALL', ...Array.from(new Set(tags))];
  }, [tasks]);

  // --- HANDLERS ---
  const handleAddItem = (name: string, quantity: number, unit: string, scope: 'personal' | 'household') => {
    const category = guessCategory(name);
    // 🤖 Fire AI categorization when local guess is generic
    if (category === 'General' && currentHouseholdId) {
      callAiAction<{ category: string }>('categorizeGroceryItem', { itemName: name }).then(result => {
        if (result?.category && result.category !== 'Other') {
          setAiCategorySuggestion({ name, category: result.category });
        }
      });
    }
    // 🛡️ Default creation scope based on current toggle (forced to personal if no household)
    // const targetScope = activeScope === 'SHARED' ? 'household' : 'personal'; // Scope now directly passed from InlineAddGrocery
    createTask(
      name,
      TemplateType.GROCERY,
      GROCERY_BOARD.id,
      [category],
      scope,
      undefined, // recurrence
      undefined, // dueDate
      undefined, // description
      undefined, // priorityTier
      undefined, // cost
      { quantity, unit, stockLevel: 1, remainingQuantity: quantity } // consumption
    );
  };

  const handleDeleteItem = (taskId: string) => {
    if (pendingDeleteId === taskId) {
      deleteTask(taskId);
      setPendingDeleteId(null);
    } else {
      setPendingDeleteId(taskId);
    }
  };

  const toggleItemScope = (taskId: string, currentScope: 'personal' | 'household' | undefined) => {
    const newScope = currentScope === 'personal' ? 'household' : 'personal';
    migrateTaskScope(taskId, newScope);
  };

  const handleSaveEdit = (taskId: string, newTitle: string, newQuantity: number, newUnit: string, newScope: 'personal' | 'household') => {
    updateTask(taskId, { 
      title: newTitle, 
      scope: newScope,
      consumption: {
        ...tasks.find(t => t.id === taskId)?.consumption, // Preserve other consumption props
        quantity: newQuantity,
        unit: newUnit,
        remainingQuantity: newQuantity // Reset remaining quantity to full stock on edit
      }
    });
    setEditingItemId(null); // Exit edit mode
  };

  const handleBatchMove = (targetStatus: TaskStatus) => {
    checkedItems.forEach((id: string) => moveTask(id, targetStatus, GROCERY_BOARD));
    setCheckedItems(new Set());
    setActiveTab(targetStatus);
  };

  // Handler to save custom categories to Firestore via dispatch
  const handleSaveCategories = (newMap: Record<string, string[]>, scope: 'personal' | 'household') => {
    dispatch({
      type: 'SAVE_CUSTOM_GROCERY_CATEGORIES', // This action type needs to be handled by your AppState reducer
      payload: {
        scope,
        categories: newMap,
        userId: currentUserId,
        householdId: currentHouseholdId, // Will be null if in personal mode
      },
    });
    setIsAdminOpen(false);
  };

  return (
    <div className="relative min-h-screen animate-in fade-in duration-500">
      {/* 🤖 AI Category Suggestion Toast */}
      {aiCategorySuggestion && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center gap-3 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl border border-slate-700">
            <Sparkles className="w-4 h-4 text-blue-400 shrink-0" />
            <span className="text-[11px] font-bold">
              {t('groceries.ai_suggestion', 'AI suggests')} <span className="text-blue-400">"{aiCategorySuggestion.category}"</span> {t('common.for', 'for')} <span className="text-white">"{aiCategorySuggestion.name}"</span>
            </span>
            <button
              onClick={() => {
                const task = tasks.find(t => t.title === aiCategorySuggestion.name);
                if (task) updateTask(task.id, { tags: [aiCategorySuggestion.category] });
                setAiCategorySuggestion(null);
              }}
              className="text-2xs font-black uppercase tracking-wider bg-brand px-3 py-1.5 rounded-xl hover:bg-brand-hover transition-colors"
            >
              {t('common.apply', 'Apply')}
            </button>
            <button onClick={() => setAiCategorySuggestion(null)} className="p-1 hover:bg-slate-700 rounded-lg transition-colors">
              <X className="w-3 h-3 text-slate-400" />
            </button>
          </div>
        </div>
      )}
      <div className={`max-w-page-full mx-auto space-y-6 p-4 pb-32 transition-all duration-300 ${isAdminOpen ? 'pr-[400px] opacity-50 lg:opacity-100' : ''}`}>
        
        {/* --- SYSTEM HEADER --- */}
        <div className="flex flex-col md:flex-row justify-between md:items-center px-2 gap-4">
           <div>
             <p className="text-2xs font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider">
               {currentHouseholdId ? t('groceries.scope_shared', 'Hybrid Supply Chain') : t('groceries.scope_personal', 'Personal Partition Mode')}
             </p>
           </div>


           <button 
             onClick={() => setIsAdminOpen(true)}
             className="hidden md:block p-3 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors"
           >
             <Settings className="w-5 h-5 text-slate-500" />
           </button>
        </div>

        {/* --- NAVIGATION TABS --- */}
        <TabBar
          tabs={([
            { id: 'todo',    label: t('groceries.shoppingList', 'Needed'), icon: <ShoppingCart className="w-4 h-4" /> },
            { id: 'pantry',  label: t('groceries.pantry', 'Stock'),         icon: <Utensils className="w-4 h-4" /> },
            { id: 'history', label: t('common.history', 'Ledger'),          icon: <History className="w-4 h-4" /> },
          ] as TabItem[])}
          activeTab={activeTab}
          onChange={id => { setActiveTab(id as any); setCheckedItems(new Set()); }}
          className="sticky top-0 z-20 shadow-sm backdrop-blur-md"
        />

        {/* --- SMART SUGGESTIONS --- */}
        {activeTab === 'todo' && suggestions.length > 0 && (
          <section className="bg-slate-900 dark:bg-blue-950 rounded-[2.5rem] p-8 text-white shadow-2xl animate-in slide-in-from-top-4">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-4 h-4 text-blue-400 animate-pulse" />
              <h2 className="text-2xs font-black uppercase tracking-[0.2em] opacity-70">
                {t('ai.suggestions', 'Cognitive Restock Protocol')}
              </h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
              {suggestions.map((s: KanbanTask) => (
                <div key={s.id} className="bg-white/5 backdrop-blur-md p-5 rounded-3xl min-w-[180px] border border-white/10 group hover:bg-white/10 transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <p className="font-black text-sm truncate uppercase italic">{s.title}</p>
                    {s.scope === 'personal' && <Lock className="w-3 h-3 text-emerald-400" />}
                  </div>
                  <button 
                    onClick={() => moveTask(s.id, 'todo', GROCERY_BOARD)}
                    className="w-full py-3 bg-white text-slate-900 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-blue-100 transition-colors"
                  >
                    {t('common.add', 'Restore')}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* --- FILTER BAR --- */}
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {categories.map((cat: string) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest whitespace-nowrap border transition-all ${selectedCategory === cat ? 'bg-brand border-brand text-white shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-400 hover:border-brand hover:text-brand dark:hover:text-brand'}`}
            >
              {cat === 'ALL' ? t('common.all_items', 'Global View') : cat}
            </button>
          ))}
        </div>

        {/* --- GROUPED CONTENT --- */}
        {Object.entries(groupedData).length === 0 && (
          <EmptyState
            icon={<ShoppingCart className="w-8 h-8 text-slate-400" />}
            title={t('groceries.empty_title', 'No items yet')}
            description={t('groceries.empty_desc', 'Add your first grocery item to get started.')}
            className="py-20"
          />
        )}
        <div className="space-y-10">
          {Object.entries(groupedData).map(([groupName, items]) => (
            <div key={groupName} className="space-y-4">
              <div className="flex items-center gap-2 px-4">
                <ChevronRight className="w-3 h-3 text-blue-500" />
                <h3 className="text-2xs font-black uppercase text-slate-400 tracking-[0.3em]">{groupName}</h3>
              </div>

              {(items as KanbanTask[]).map((item: KanbanTask) => (
                <div key={item.id} className={`bg-white dark:bg-slate-900 rounded-2xl p-5 border shadow-sm hover:shadow-md transition-all group ${item.scope === 'personal' ? 'border-emerald-100 dark:border-emerald-900/30' : 'border-slate-100 dark:border-slate-800'}`}>
                  {editingItemId === item.id ? (
                    <EditGroceryItem
                      item={item}
                      onSave={handleSaveEdit}
                      onCancel={() => setEditingItemId(null)}
                      currentHouseholdId={currentHouseholdId}
                    />
                  ) : (
                    <div className="flex flex-col gap-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-5">
                          <button
                            onClick={() => {
                              const next = new Set(checkedItems);
                              next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                              setCheckedItems(next);
                            }}
                            className={`w-8 h-8 rounded-xl border-2 flex items-center justify-center transition-all ${checkedItems.has(item.id) ? 'bg-brand border-brand text-white shadow-lg' : 'border-slate-200 dark:border-slate-800 group-hover:border-blue-300'}`}
                          >
                            {checkedItems.has(item.id) && <Check className="w-5 h-5" />}
                          </button>
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className={`font-bold text-lg transition-all ${checkedItems.has(item.id) ? 'line-through text-slate-300' : 'text-slate-900 dark:text-white'}`}>
                                {item.title}
                              </span>
                              {item.scope === 'personal' && <Lock className="w-3 h-3 text-emerald-500 opacity-50" />}
                            </div>
                            {item.consumption && (
                              <p className="text-2xs font-black text-slate-400 uppercase tracking-widest mt-1">
                                {item.consumption.quantity} {item.consumption.unit}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="w-20 h-2 bg-slate-50 dark:bg-slate-800 rounded-full overflow-hidden hidden sm:block">
                            <div
                              className={`h-full transition-all duration-1000 ${(item.consumption?.stockLevel || 0) < 0.3 ? 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.4)]' : 'bg-blue-500 shadow-[0_0_10px_theme(colors.blue.500/0.4)]'}`}
                              style={{ width: `${Math.min(100, (item.consumption?.stockLevel || 0) * 100)}%` }}
                            />
                          </div>
                          <span className="text-2xs font-black text-slate-400 uppercase tabular-nums">
                            {Math.round((item.consumption?.stockLevel || 0) * 100)}%
                          </span>
                           <button
                            onClick={() => toggleItemScope(item.id, item.scope)}
                            className="p-2 rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            title={item.scope === 'personal' ? 'Switch to Shared' : 'Switch to Personal'}
                          >
                            {item.scope === 'personal' ? <Shield className="w-4 h-4 text-emerald-500" /> : <Users className="w-4 h-4 text-blue-500" />}
                          </button>
                          <button
                            onClick={() => setEditingItemId(item.id)}
                            className="p-2 rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            title="Edit Item"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {pendingDeleteId === item.id ? (
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              onBlur={() => setPendingDeleteId(null)}
                              className="px-2 py-1 rounded text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white transition-colors"
                              autoFocus
                            >
                              {t('common.confirm', 'Confirm')}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="p-2 rounded-full text-slate-400 hover:bg-rose-100 dark:hover:bg-rose-900/20 hover:text-rose-500 transition-colors"
                              title={t('groceries.confirm_delete', 'Delete item')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {activeTab === 'pantry' && item.consumption && (
                        <div className="flex items-center justify-between pt-4 border-t border-slate-50 dark:border-slate-800/50">
                          <div className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800 p-1.5 rounded-2xl">
                              <button
                                onClick={() => consumeQuantity(item.id, 0.25)}
                                className="px-4 py-2 text-[9px] font-black text-rose-500 hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-all uppercase tracking-widest"
                              >
                                -0.25 {item.consumption.unit}
                              </button>
                              <div className="h-4 w-px bg-slate-200 dark:bg-slate-700 mx-1" />
                              <button
                                onClick={() => consumeQuantity(item.id, 1)}
                                className="px-4 py-2 text-[9px] font-black text-rose-500 hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-all uppercase tracking-widest"
                              >
                                -1 {item.consumption.unit}
                              </button>
                          </div>
                          <span className="text-2xs font-black text-slate-400 uppercase tracking-widest">
                            {item.consumption.remainingQuantity} / {item.consumption.quantity} {item.consumption.unit}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          <div className="pt-6">
            <InlineAddGrocery 
            onAdd={handleAddItem} 
            historyTasks={tasks} 
            currentHouseholdId={currentHouseholdId}
            activeDataScope={activeScope}
          />
          </div>
        </div>

        {/* --- ACTION BAR --- */}
        {checkedItems.size > 0 && (
          <div className="fixed bottom-10 left-1/2 -translate-x-1/2 w-full max-w-sm px-6 animate-in slide-in-from-bottom-10 z-50">
            <Button 
              onClick={() => handleBatchMove(activeTab === 'todo' ? 'pantry' : 'history')}
              className={`w-full h-16 shadow-2xl rounded-full text-xs font-black gap-3 text-white uppercase tracking-[0.2em] transition-all transform active:scale-95 ${activeTab === 'todo' ? 'bg-brand hover:bg-brand-hover shadow-blue-500/40' : 'bg-rose-600 hover:bg-rose-700 shadow-rose-500/40'}`}
            >
              {activeTab === 'todo' ? <CheckCircle2 className="w-5 h-5" /> : <Pizza className="w-5 h-5" />}
              {activeTab === 'todo' ? t('groceries.complete_shop', 'Sync to Pantry') : t('groceries.log_consumption', 'Mark Expended')} ({checkedItems.size})
            </Button>
          </div>
        )}
      </div>

      {/* --- ADMIN DRAWER --- */}
      {isAdminOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div 
            className="absolute inset-0 bg-slate-900/60 animate-in fade-in duration-300" 
            onClick={() => setIsAdminOpen(false)} 
          />
          <CategoryManager
            onClose={() => setIsAdminOpen(false)}
            onSave={handleSaveCategories}
            currentHouseholdId={currentHouseholdId}
            isAdmin={true}
          />
        </div>
      )}
    </div>
  );
};